import { useState, useEffect } from 'react';
import { useTermsGatekeeper } from '@/hooks/useTermsGatekeeper';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ShieldAlert, FileText, ChevronRight, Info } from 'lucide-react';
import { Callout } from '@/components/ui/callout';

export const TermsGatekeeper = ({ children }: { children: React.ReactNode }) => {
    const { status, params, acceptTerms, isAccepting, refetch } = useTermsGatekeeper();

    const [aceitouUso, setAceitouUso] = useState(false);
    const [aceitouPrivacidade, setAceitouPrivacidade] = useState(false);
    const [aceitouTratamento, setAceitouTratamento] = useState(false);
    const [aceitouRegras, setAceitouRegras] = useState(false);
    const [clientIp, setClientIp] = useState<string>('0.0.0.0');

    const [lembrarMaisTarde, setLembrarMaisTarde] = useState(false); // Para soft block

    // Só busca IP quando o modal de aceite REALMENTE precisa ser exibido (raro).
    // Antes: rodava em toda montagem, bloqueando carregamento inicial das telas.
    const precisaIp = status === 'blocked_hard' || status === 'blocked_soft';
    useEffect(() => {
        if (!precisaIp || clientIp !== '0.0.0.0') return;
        fetch('https://api.ipify.org?format=json')
            .then((res) => res.json())
            .then((data) => setClientIp(data.ip))
            .catch((err) => console.error('Erro ao buscar IP', err));
    }, [precisaIp, clientIp]);

    if (status === 'loading') {
        // Pode renderizar um esqueleto ou spinner simples
        return <div className="flex h-screen w-full items-center justify-center bg-background"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
    }

    // Bypass, Permissão, Erro, ou se o usuário escolheu lembrar mais tarde (só no soft block)
    if (status === 'allowed' || status === 'bypass' || lembrarMaisTarde || status === 'error') {
        return <>{children}</>;
    }

    const todasAceitas = aceitouUso && aceitouPrivacidade && aceitouTratamento && aceitouRegras;
    const isHardBlock = status === 'blocked_hard';

    const handleConfirmar = async () => {
        if (!params) return;
        try {
            await acceptTerms({
                versao: params.versao_atual,
                ip: clientIp,
                userAgent: navigator.userAgent,
            });
            // Após sucesso, o próprio hook vai invalidar a query e o status passará a 'allowed'
        } catch (e) {
            console.error(e);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
            <div className="bg-card w-full max-w-2xl rounded-xl border shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
                {/* Header */}
                <div className="bg-primary/5 border-b p-6 flex flex-col items-center justify-center text-center">
                    <div className="bg-primary/20 p-3 rounded-full mb-4">
                        <ShieldAlert className="h-8 w-8 text-primary" />
                    </div>
                    <h2 className="text-2xl font-bold tracking-tight">Atualização de Políticas e Privacidade</h2>
                    <p className="text-muted-foreground mt-2 max-w-md">
                        Visando maior transparência e segurança (LGPD), nossos termos de uso e privacidade foram atualizados.
                    </p>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto flex-1 flex flex-col gap-6">

                    {isHardBlock ? (
                        <Callout variant="destructive" icon={<ShieldAlert />} title="Ação Obrigatória">
                            Você não aceitou os termos de uso ou políticas obrigatórias. O acesso não será permitido até a conformidade total.
                        </Callout>
                    ) : (
                        <Callout variant="info" icon={<Info />} title="Aviso">
                            Novos termos estão disponíveis. O aceite será obrigatório a partir de {params?.obrigatorio_desde ? new Date(params.obrigatorio_desde).toLocaleDateString('pt-BR') : 'breve'}.
                        </Callout>
                    )}

                    <div className="flex flex-col gap-4">
                        {/* Checkbox 1 */}
                        <div className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-accent/50 transition-colors">
                            <Checkbox
                                id="termos_uso"
                                checked={aceitouUso}
                                onCheckedChange={(c) => setAceitouUso(c as boolean)}
                                className="mt-1"
                            />
                            <div className="grid gap-1.5 leading-none w-full">
                                <label htmlFor="termos_uso" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer">
                                    Li e aceito os Termos de Uso
                                </label>
                                <div className="flex justify-between items-center text-sm text-muted-foreground w-full">
                                    <span>Concordo com as condições gerais de utilização do sistema.</span>
                                    {params?.url_termos_uso && (
                                        <a href={params.url_termos_uso} target="_blank" rel="noopener noreferrer" className="flex items-center text-primary hover:underline font-medium ml-2 shrink-0">
                                            <FileText className="h-3 w-3 mr-1" />
                                            Ler Documento
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Checkbox 2 */}
                        <div className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-accent/50 transition-colors">
                            <Checkbox
                                id="politica_privacidade"
                                checked={aceitouPrivacidade}
                                onCheckedChange={(c) => setAceitouPrivacidade(c as boolean)}
                                className="mt-1"
                            />
                            <div className="grid gap-1.5 leading-none w-full">
                                <label htmlFor="politica_privacidade" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer">
                                    Concordo com a Política de Privacidade
                                </label>
                                <div className="flex justify-between items-center text-sm text-muted-foreground w-full">
                                    <span>Estou ciente de como o EduLumina coleta informações.</span>
                                    {params?.url_politica_privacidade && (
                                        <a href={params.url_politica_privacidade} target="_blank" rel="noopener noreferrer" className="flex items-center text-primary hover:underline font-medium ml-2 shrink-0">
                                            <FileText className="h-3 w-3 mr-1" />
                                            Ler Documento
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Checkbox 3 */}
                        <div className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-accent/50 transition-colors">
                            <Checkbox
                                id="tratamento_dados"
                                checked={aceitouTratamento}
                                onCheckedChange={(c) => setAceitouTratamento(c as boolean)}
                                className="mt-1"
                            />
                            <div className="grid gap-1.5 leading-none w-full">
                                <label htmlFor="tratamento_dados" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer">
                                    Autorizo o tratamento de dados pessoais conforme descrito
                                </label>
                                <div className="flex justify-between items-center text-sm text-muted-foreground w-full">
                                    <span>Dou consentimento para o processamento dos meus dados (LGPD).</span>
                                    {params?.url_tratamento_dados && (
                                        <a href={params.url_tratamento_dados} target="_blank" rel="noopener noreferrer" className="flex items-center text-primary hover:underline font-medium ml-2 shrink-0">
                                            <FileText className="h-3 w-3 mr-1" />
                                            Ler Documento
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Checkbox 4 */}
                        <div className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-accent/50 transition-colors">
                            <Checkbox
                                id="regras_instituicao"
                                checked={aceitouRegras}
                                onCheckedChange={(c) => setAceitouRegras(c as boolean)}
                                className="mt-1"
                            />
                            <div className="grid gap-1.5 leading-none w-full">
                                <label htmlFor="regras_instituicao" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer">
                                    Declaro estar ciente das Regras Institucionais
                                </label>
                                <div className="flex justify-between items-center text-sm text-muted-foreground w-full">
                                    <span>Comprometo-me a seguir as diretrizes da minha instituição.</span>
                                    {params?.url_regras_institucionais && (
                                        <a href={params.url_regras_institucionais} target="_blank" rel="noopener noreferrer" className="flex items-center text-primary hover:underline font-medium ml-2 shrink-0">
                                            <FileText className="h-3 w-3 mr-1" />
                                            Ler Documento
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>

                    </div>
                </div>

                {/* Footer */}
                <div className="border-t bg-muted/30 p-6 flex items-center justify-between">
                    {!isHardBlock ? (
                        <Button variant="ghost" onClick={() => setLembrarMaisTarde(true)}>
                            Lembrar mais tarde
                        </Button>
                    ) : (
                        <div /> // Espaço vazio para manter o alinhamento
                    )}

                    <Button
                        disabled={!todasAceitas || isAccepting}
                        onClick={handleConfirmar}
                        size="lg"
                        className="w-full sm:w-auto"
                    >
                        {isAccepting ? 'Confirmando...' : 'Confirmar e Acessar o Sistema'}
                        {!isAccepting && <ChevronRight className="ml-2 h-4 w-4" />}
                    </Button>
                </div>
            </div>
        </div>
    );
};
